/** Route path for the forced DOT-physical booking gate. */
export const DOT_BOOKING_GATE_PATH = "/dot-booking-gate";

/**
 * Query param carrying the lesson path the student was heading to. The gate
 * navigates here (with `replace`) once the MEDICAL_EXAM milestone is written.
 */
export const DOT_GATE_RETURN_PARAM = "next";

/**
 * Lesson whose completion write immediately preceded the redirect. The gate
 * uses it to bridge the short Firestore snapshot race after `markCompleted()`.
 */
export const DOT_GATE_COMPLETED_LESSON_PARAM = "completed";

/**
 * Query param recording which study action diverted the student here:
 * `lesson_advance` (the `navigateForward` divert after completing video 6) or
 * `lesson_start` (opening a lesson page while at exactly 6 completed videos).
 * Unlike the permit gate there is no quiz-start surface: this experiment
 * interrupts the 7th VIDEO specifically, and its new-students-only band means
 * there is no stalled back-catalog to reach.
 */
export const DOT_GATE_TRIGGER_PARAM = "trigger";

export const DOT_GATE_TRIGGERS = ["lesson_advance", "lesson_start"] as const;

export type DotGateTrigger = typeof DOT_GATE_TRIGGERS[number];

/**
 * Completed-video band that opens the gate: at least
 * `DOT_GATE_AFTER_COMPLETED_LESSONS` (the student is about to start their 7th
 * video) and strictly below `DOT_GATE_NEW_STUDENT_LESSON_LIMIT`.
 *
 * The lower bound is `>=` for the same snapshot-lag reason as the permit gate
 * (`markCompleted()` can precede the live count by a tick). The upper bound is
 * what makes this a NEW-students-only experiment: anyone already at 7+
 * completed videos when the experiment launches has "hit 7 videos" and is
 * bypassed. Within the band the gate is self-limiting — a gated test-arm
 * student cannot start another video, so their count stays at 6 until a
 * MEDICAL_EXAM milestone releases them.
 *
 * 7 of 21 CDL videos is the one-third mark the momentum headline references.
 */
export const DOT_GATE_AFTER_COMPLETED_LESSONS = 6;
export const DOT_GATE_NEW_STUDENT_LESSON_LIMIT = 7;

/**
 * Soft-gate mode marker stamped on the enrollment doc and carried on every
 * gate analytics event from the 2026-09-20 softening onward. Readouts MUST
 * segment on it: before this the gate was a hard wall (no skip), so pooled
 * hard+soft numbers describe neither treatment.
 */
export const DOT_GATE_MODE_SOFT = "soft";

/**
 * Idle countdown before the momentum interstitial carries the student into
 * their lesson anyway. First encounter gets longer — reading the pitch and
 * the funds promise is gestureless on a phone, and week-1 showed the booking
 * win happens inside the first encounter (median gate→book 1–2 minutes), so
 * that screen earns more patience. Repeat encounters know the drill.
 */
export const DOT_GATE_SOFT_FIRST_ENCOUNTER_SECONDS = 30;
export const DOT_GATE_SOFT_REPEAT_ENCOUNTER_SECONDS = 10;

/**
 * How long a "kept studying" exit holds off the next divert on this device.
 * At most one ask per day, without rebuilding the wall — the welcome-back
 * card's readout showed dismiss-fatigue climbing steeply past one impression
 * per day. Note the band exit (experiment.ts): a student who studies through
 * video 7 during the pass leaves the eligibility band and is never re-asked,
 * so only walk-away students actually see tomorrow's ask.
 */
export const DOT_GATE_SOFT_PASS_HOURS = 24;

/**
 * localStorage key prefix for the soft pass, `${prefix}:{userId}` — the
 * `emerge.` namespace matches the repo's other per-user latches (video-speed,
 * welcome-back). Renaming this after launch orphans live passes and re-gates
 * students a day early.
 */
export const DOT_GATE_SOFT_PASS_KEY_PREFIX = "emerge.dotGate.softPass";

/**
 * Tentative milestone date written when a student self-attests a booking
 * without producing the exact date ("I scheduled it but don't have the
 * date"). A week out keeps the plan honest enough for coach follow-up; the
 * congrats screen already tells the student to ask their coach to move it.
 */
export const DOT_GATE_ATTESTED_TENTATIVE_DAYS = 7;

/**
 * Title used for the MEDICAL_EXAM milestone the gate's date-picker path
 * writes. Matches what `PermitMilestoneNudgeCard`'s DotStep flow writes today,
 * so downstream surfaces (plan cards, Dial) see the familiar title. The CityMD
 * vendor path writes its own title inside `PlanVisitDateStep`, unchanged.
 */
export const DOT_MILESTONE_TITLE = "DOT Physical";
